import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { forkJoin, Observable, ReplaySubject, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, exhaustMap, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import * as _ from 'underscore';
import { ConfirmDailogueComponent } from 'src/app/shared/components/dailogue/confirm-dailogue.component';
import { Api } from 'src/app/shared/constants/api';
import { APP_ROUTES } from 'src/app/shared/constants/approutes';
import { Message } from 'src/app/shared/constants/message';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { IPermissionWithShow } from 'src/app/shared/interface';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { SharedFormService } from 'src/app/shared/services/SharedFormService.service';
import { ICustomerServiceRequirementDetail } from '../customer-service-requirement.interface';
import { CsrRemarkDialogComponent, ICsrRemarkDialogData, ICsrRemarkDialogResult } from '../csr-remark-dialog/csr-remark-dialog.component';
import { ServiceFormComponent } from '../../service/service-form/service-form.component';

@Component({
    selector: 'app-customer-service-requirement-form',
    templateUrl: './customer-service-requirement-form.component.html',
    styleUrls: ['./customer-service-requirement-form.component.less'],
    standalone: false
})
export class CustomerServiceRequirementFormComponent implements OnInit, OnDestroy {

  form: FormGroup;
  businessSegments: any[] = [];
  customers: any[] = [];
  destinations: any[] = [];

  branches: any[] = [];

  filteredBusinessSegments: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  filteredCustomers: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  filteredRowShippers: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  filteredRowConsignees: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  filteredBranches: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  filteredDestinations: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  filteredServices: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  /** First-page (20 records) cache — used to restore the dropdown after search is cleared.
   *  Public so the template can use them for tooltip lookups. */
  initialShippers: any[] = [];
  initialConsignees: any[] = [];
  initialServices: any[] = [];

  bsSearchCtrl = this.formBuilder.control('');
  customerSearchCtrl = this.formBuilder.control('');
  shipperSearchCtrl: FormControl = this.formBuilder.control('');
  consigneeSearchCtrl: FormControl = this.formBuilder.control('');
  serviceSearchCtrl = this.formBuilder.control('');
  branchSearchCtrl = this.formBuilder.control('');
  rowDestinationSearchCtrl = this.formBuilder.control('');

  /** Separate controls for header column filters (filter table rows by selected value name).
   *  Kept separate from dropdown search controls to avoid clearing selected display values. */
  headerShipperFilter = this.formBuilder.control('');
  headerConsigneeFilter = this.formBuilder.control('');
  headerDestinationFilter = this.formBuilder.control('');
  headerServiceFilter = this.formBuilder.control('');

  addPermission: boolean = false;
  editPermission: boolean = false;
  disableSubmit: boolean = false;
  isEditedData: boolean = false;

  /** Controls visibility of the audit history panel */
  viewLog = this.formBuilder.control(false);

  readonly processAuditData = (auditData: any[]): any[] => {
    const fieldLabelMap: Record<string, string> = {
      businessSegmentName: 'Business Segment',
      customerName:        'Customer',
      customerBranchName:  'Customer Branch',
      status:              'Status',
      activeFlag:          'Active',
      shipperName:         'Shipper',
      consigneeName:       'Consignee',
      destinationName:     'Destination',
      serviceCode:         'Service Code',
      serviceDescription:  'Service Description',
      sBillWise:           'S-Bill Wise',
      hblWise:             'HBL Wise',
    };
    return (auditData ?? []).map(item => {
      if (!item?.propertyName) return item;
      const segments = item.propertyName.split('.');
      const lastSegment = [...segments].reverse().find(s => !/^\d+$/.test(s));
      return { ...item, propertyName: fieldLabelMap[lastSegment] ?? item.propertyName };
    });
  };

  @Input() searching: boolean = false;
  onDestroy = new Subject<void>();

  /** Keys of rows that have a duplicate (Shipper~Consignee~Destination~Service). Used for UI highlighting. */
  duplicateRowIndices: Set<number> = new Set();

  /** Auto-incrementing counter used to assign a stable unique rowId to each new detail row. */
  private rowCounter = 0;

  /** Indices of rows where neither S-Bill nor HBL is selected. Cleared on radio change, populated on save attempt. */
  billTypeErrorIndices: Set<number> = new Set();

  /** Remark detail objects for unsaved rows, keyed by stable rowId (not index).
   *  rowId never shifts when other rows are added or removed.
   *  Posted after the main record is saved using composite-key matching. */
  pendingRemarks: Map<number, any[]> = new Map();

  /** True when returning from Direct Entity add — skips getById() to preserve saved state. */
  private hasSavedCsrState = false;

  constructor(private formBuilder: FormBuilder, private activatedRoute: ActivatedRoute,
    private router: Router, private utilityService: UtilityService,
    private service: DataService, private permissionService: PermissionService,
    private logger: NGXLogger, private dialog: MatDialog,
    private sharedFormService: SharedFormService) { }

  ngOnInit(): void {
    this.getAllPermissions();
    this.initializeForm();
    this.setupCustomerChange();
    this.setupBranchDuplicateCheck();
    this.activatedRoute.params.subscribe((param: Params) => {
      if (+param['id']) {
        this.id.setValue(+param['id']);
      }
    });
    this.getServices();
    this.filterBusinessSegments();
    this.filterCustomers();
    this.filterRowShippers();
    this.filterRowConsignees();
    this.filterRowServices();
    this.filterBranches();
    this.filterRowDestinations();
    this.loadDropdownsAndInit();
  }

  ngOnDestroy() {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      id: [],
      businessSegmentId: [null, [Validators.required]],
      customerId: [null, [Validators.required]],
      customerBranchId: [null, [Validators.required]],
      customerServiceRequirementDetails: this.formBuilder.array([this.buildDetailRow()])
    });
  }

  private setupCustomerChange() {
    this.customerId.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(customerId => {
      this.customerBranchId.setValue(null, { emitEvent: false });
      this.branches = [];
      this.filteredBranches.next([]);
      if (customerId) {
        this.getCustomerBranches(customerId);
      }
    });
  }

  private setupBranchDuplicateCheck() {
    this.customerBranchId.valueChanges.pipe(
      takeUntil(this.onDestroy),
      debounceTime(300),
      switchMap(branchId => {
        const bsId = this.businessSegmentId.value;
        const custId = this.customerId.value;
        if (!branchId || !bsId || !custId) return of(null);
        return this.service.get(Api.CUSTOMER_SERVICE_REQUIREMENT, {
          businessSegmentId: bsId,
          customerId: custId,
          customerBranchId: branchId,
          size: 1,
          page: 0
        });
      })
    ).subscribe(res => {
      if (!res) return;
      const records: any[] = res.contents || res || [];
      const currentId = this.id.value;
      const isDuplicate = records.some((r: any) => r.id !== currentId);
      if (isDuplicate) {
        this.customerBranchId.setValue(null, { emitEvent: false });
        this.utilityService.notifier(
          'The record for combination of Business segment, Customer and Customer Branch already exists.',
          'error'
        );
      }
    });
  }

  buildDetailRow(data?: ICustomerServiceRequirementDetail): FormGroup {
    return this.formBuilder.group({
      rowId: [++this.rowCounter],
      id: [data?.id || null],
      shipperId: [data?.shipperId || null],
      consigneeId: [data?.consigneeId || null],
      destinationId: [data?.destinationId || null],
      serviceId: [data?.serviceId || null, [Validators.required]],
      sBillWise: [data?.sBillWise ?? true],
      hblWise: [data?.hblWise ?? false],
      remarksCount: [data?.remarksCount || 0],
      lockedAfterUseCsrService: [data?.lockedAfterUseCsrService || false],
      displayCreatedBy: [{ value: data?.displayCreatedBy || null, disabled: true }],
      displayCreatedDate: [{ value: data?.displayCreatedDate || null, disabled: true }]
    });
  }

  trackByControl(_index: number, control: AbstractControl): AbstractControl { return control; }

  trackByEntry(_index: number, entry: { index: number; control: AbstractControl }): AbstractControl {
    return entry.control;
  }

  /** Returns details controls filtered by the header column search inputs.
   *  Uses original indices so formGroupName and row actions remain correct. */
  get filteredDetailEntries(): { index: number; control: AbstractControl }[] {
    const s   = (this.headerShipperFilter.value   || '').toLowerCase().trim();
    const c   = (this.headerConsigneeFilter.value  || '').toLowerCase().trim();
    const d   = (this.headerDestinationFilter.value || '').toLowerCase().trim();
    const svc = (this.headerServiceFilter.value    || '').toLowerCase().trim();

    return this.details.controls
      .map((control, index) => ({ index, control }))
      .filter(({ control }) => {
        if (s) {
          const id = control.get('shipperId')?.value;
          const name = (this.initialShippers.find(x => x.id === id)?.name || '').toLowerCase();
          if (!name.includes(s)) return false;
        }
        if (c) {
          const id = control.get('consigneeId')?.value;
          const name = (this.initialConsignees.find(x => x.id === id)?.name || '').toLowerCase();
          if (!name.includes(c)) return false;
        }
        if (d) {
          const id = control.get('destinationId')?.value;
          const dest = this.destinations.find(x => x.id === id);
          const name = (dest?.name || '').toLowerCase();
          if (!name.includes(d)) return false;
        }
        if (svc) {
          const id = control.get('serviceId')?.value;
          const service = this.initialServices.find(x => x.id === id);
          const label = ((service?.serviceCode || '') + ' ' + (service?.serviceDescription || '')).toLowerCase();
          if (!label.includes(svc)) return false;
        }
        return true;
      });
  }

  addRow() {
    const topRow = this.details.at(0);
    const isEmpty = !topRow.get('id').value
      && !topRow.get('shipperId').value
      && !topRow.get('consigneeId').value
      && !topRow.get('destinationId').value
      && !topRow.get('serviceId').value;
    if (isEmpty) {
      this.utilityService.notifier('Please fill in the current empty row before adding a new one.', 'warning');
      return;
    }
    this.details.insert(0, this.buildDetailRow());
    this.billTypeErrorIndices.clear();
    this.form.markAsDirty();
  }

  removeRow(index: number) {
    if (this.details.at(index).get('lockedAfterUseCsrService').value) {
      this.utilityService.notifier('This row is already in use in a transaction and cannot be removed.', 'warning');
      return;
    }
    if (this.details.length === 1) {
      this.utilityService.notifier('At least one row is required.', 'warning');
      return;
    }
    // Remove pending remarks by rowId before removing the row (rowId is stable, index is not)
    const rowId = this.details.at(index).get('rowId')?.value;
    if (rowId != null) this.pendingRemarks.delete(rowId);
    this.details.removeAt(index);
    this.checkDuplicateRows();
    // Shift billTypeErrorIndices down after removal (these are still index-based for UI only)
    const shifted = new Set<number>();
    this.billTypeErrorIndices.forEach(i => { if (i < index) shifted.add(i); else if (i > index) shifted.add(i - 1); });
    this.billTypeErrorIndices = shifted;
    this.form.markAsDirty();
  }

  copyRow(index: number) {
    const rowValue = (this.details.at(index) as FormGroup).getRawValue();
    const copy = this.buildDetailRow({
      ...rowValue,
      id: null,
      remarksCount: 0,
      lockedAfterUseCsrService: false,
      displayCreatedBy: null,
      displayCreatedDate: null
    });
    this.details.insert(index + 1, copy);
    this.checkDuplicateRows();
    // Shift error indices up for rows after the inserted position
    const shifted = new Set<number>();
    this.billTypeErrorIndices.forEach(i => shifted.add(i <= index ? i : i + 1));
    this.billTypeErrorIndices = shifted;
    this.form.markAsDirty();
  }

  onRadioChange(index: number, field: 'sBillWise' | 'hblWise') {
    const row = this.details.at(index);
    if (field === 'sBillWise') {
      row.get('sBillWise').setValue(true);
      row.get('hblWise').setValue(false);
    } else {
      row.get('hblWise').setValue(true);
      row.get('sBillWise').setValue(false);
    }
    this.billTypeErrorIndices.delete(index);
    this.form.markAsDirty();
  }

  /** Real-time duplicate check called whenever Shipper/Consignee/Destination/Service changes. */
  checkDuplicateRows() {
    this.duplicateRowIndices.clear();
    const keys = this.details.controls.map((ctrl, _i) => {
      const v = ctrl.value;
      return (v.shipperId ?? 'NULL') + '~' + (v.consigneeId ?? 'NULL') + '~'
        + (v.destinationId ?? 'NULL') + '~' + (v.serviceId ?? 'NULL');
    });
    const seen = new Map<string, number>();
    keys.forEach((key, i) => {
      if (seen.has(key)) {
        this.duplicateRowIndices.add(i);
        this.duplicateRowIndices.add(seen.get(key));
      } else {
        seen.set(key, i);
      }
    });
  }

  openRemarksDialog(index: number) {
    const detailId = this.details.at(index).get('id').value;
    const rowId: number = this.details.at(index).get('rowId').value;
    const data: ICsrRemarkDialogData = {
      detailId,
      isFormDisabled: this.form.disabled,
      pendingRemarkDetails: !detailId ? (this.pendingRemarks.get(rowId) || []) : []
    };
    const dialogRef = this.dialog.open(CsrRemarkDialogComponent, {
      data,
      panelClass: 'csr-remark-dialog-panel'
    });
    dialogRef.afterClosed().subscribe((result: ICsrRemarkDialogResult | null) => {
      if (!result) return;
      if (result.count !== undefined) {
        // Remarks were saved to API (row already had an id) — clean up any stale pending entry
        this.details.at(index).get('remarksCount').setValue(result.count);
        this.pendingRemarks.delete(rowId);
        this.form.markAsDirty();
      } else if (result.pendingDetails?.length) {
        // Row was unsaved — merge with any existing pending remarks (keyed by stable rowId)
        const existing = this.pendingRemarks.get(rowId) || [];
        const merged = [...existing, ...result.pendingDetails];
        this.pendingRemarks.set(rowId, merged);
        this.details.at(index).get('remarksCount').setValue(merged.length);
        this.form.markAsDirty();
      }
    });
  }

  private postPendingRemarks(savedDetails: any[]): Observable<any> {
    if (!this.pendingRemarks.size) return of(null);
    const calls: Observable<any>[] = [];
    this.pendingRemarks.forEach((remarks, rowId) => {
      if (!remarks.length) return;
      // Find the FormGroup with this rowId (stable — not affected by row add/remove/reorder)
      const formRow = this.details.controls.find(ctrl => ctrl.get('rowId')?.value === rowId);
      if (!formRow) return;
      // Read the four identifying fields from the form row
      const serviceId   = formRow.get('serviceId')?.value   ?? null;
      const shipperId   = formRow.get('shipperId')?.value   || null;
      const consigneeId = formRow.get('consigneeId')?.value || null;
      const destinationId = formRow.get('destinationId')?.value || null;
      // Match the saved detail by composite key — order of savedDetails does not matter
      const match = savedDetails.find(d =>
        d.serviceId === serviceId &&
        (d.shipperId   || null) === shipperId &&
        (d.consigneeId || null) === consigneeId &&
        (d.destinationId || null) === destinationId
      );
      if (match?.id) {
        calls.push(this.service.post(Api.REMARKS, {
          referenceType: 'CUSTOMER_SERVICE_REQUIREMENT_DETAIL',
          parentReferenceId: match.id,
          parentPageRefType: 'CUSTOMER_SERVICE_REQUIREMENT',
          remarkDetails: remarks
        }));
      }
    });
    return calls.length ? forkJoin(calls) : of(null);
  }

  onSave() {
    this.form.markAllAsTouched();

    // Validate S-Bill / HBL selection per row and mark visually
    this.billTypeErrorIndices.clear();
    this.details.controls.forEach((row, i) => {
      if (!row.get('sBillWise').value && !row.get('hblWise').value) {
        this.billTypeErrorIndices.add(i);
      }
    });

    if (this.duplicateRowIndices.size > 0) {
      this.utilityService.notifier('Duplicate row found: same Shipper, Consignee, Destination and Service combination already exists.', 'error');
      return;
    }
    if (this.billTypeErrorIndices.size > 0) {
      this.utilityService.notifier('Please select S-Bill or HBL for each service row.', 'error');
      return;
    }
    if (this.form.valid) {
      this.isEditedData = true;
      this.openDialog(this.id.value ? Message.SAVE_OPEN_DAILOG_IN_EDIT : Message.SAVE_OPEN_DAILOG);
    }
  }

  saveModule() {
    this.form.markAllAsTouched();
    const method = this.id.value ? 'put' : 'post';
    const message = this.id.value ? 'updated' : 'saved';
    if (this.form.valid) {
      this.form.markAsUntouched();
      this.service[method](Api.CUSTOMER_SERVICE_REQUIREMENT, this.form.getRawValue()).subscribe((res: any) => {
        this.utilityService.notifier('Customer Service Requirement ' + message + ' successfully', 'success');
        const savedDetails = res?.customerServiceRequirementDetails || [];
        this.postPendingRemarks(savedDetails).subscribe(() => {
          this.router.navigate([APP_ROUTES.CUSTOMER_SERVICE_REQUIREMENT]);
        });
      });
    }
  }

  onCancel() {
    if (this.form.dirty) {
      this.isEditedData = false;  // ensure cancel always navigates, never triggers save
      this.openDialog(Message.CANCEL_OPEN_DAILOG_IN_EDIT);
    } else {
      this.router.navigate([APP_ROUTES.CUSTOMER_SERVICE_REQUIREMENT]);
    }
  }

  private openDialog(message?: string) {
    const dialogRef = this.dialog.open(ConfirmDailogueComponent, {
      data: message,
      maxWidth: '300px'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === false) { this.isEditedData = false; return; }
      if (!this.isEditedData) {
        this.router.navigate([APP_ROUTES.CUSTOMER_SERVICE_REQUIREMENT]);
      } else {
        this.saveModule();
      }
    });
  }

  private getById() {
    if (this.id.value) {
      this.service.get(Api.CUSTOMER_SERVICE_REQUIREMENT + '/' + this.id.value).subscribe(res => {
        this.logger.info('CustomerServiceRequirement by Id', res);
        if (!this.editPermission) {
          this.form.disable();
          this.disableSubmit = true;
        }
        if (res.customerId) {
          this.getCustomerBranches(res.customerId);
        }
        this.form.patchValue(res, { emitEvent: false });
        this.patchDetails(res);
        this.ensureSelectedEntitiesVisible();
        this.ensureSelectedDestinationsVisible();
        this.ensureSelectedServicesVisible();
        this.syncRemarksCounts();
      });
    }
  }

  /** Fetches actual remarks count from the remarks API for each saved detail row
   *  and syncs the badge — corrects any stale DB counter. */
  private syncRemarksCounts() {
    this.details.controls.forEach((row) => {
      const detailId = row.get('id')?.value;
      if (!detailId) return;
      this.service.get(`${Api.REMARKS}/reference/CUSTOMER_SERVICE_REQUIREMENT_DETAIL/${detailId}`)
        .subscribe(res => {
          if (res && Array.isArray(res.remarkDetails)) {
            row.get('remarksCount').setValue(res.remarkDetails.length, { emitEvent: false });
          }
        }, () => { /* remarks count is non-critical — suppress error toast */ });
    });
  }

  private patchDetails(res: any) {
    this.details.clear();
    const isViewMode = this.form.disabled;
    if (res.customerServiceRequirementDetails && res.customerServiceRequirementDetails.length > 0) {
      const sorted = [...res.customerServiceRequirementDetails].sort((a: any, b: any) => (b.id || 0) - (a.id || 0));
      sorted.forEach((detail: ICustomerServiceRequirementDetail) => {
        const row = this.buildDetailRow(detail);
        if (detail.lockedAfterUseCsrService || isViewMode) {
          row.disable();
        }
        this.details.push(row);
      });
    } else {
      this.details.push(this.buildDetailRow());
    }

    // Once ANY detail row has been used in a transaction, lock the header too — Business Segment /
    // Customer / Customer Branch can no longer change (they define which shipments the used rows
    // matched). New service rows can still be added. Values are kept on save via getRawValue().
    const hasLockedDetail = this.details.controls.some(c => c.get('lockedAfterUseCsrService').value);
    if (hasLockedDetail && !isViewMode) {
      this.businessSegmentId.disable({ emitEvent: false });
      this.customerId.disable({ emitEvent: false });
      this.customerBranchId.disable({ emitEvent: false });
    }

    this.form.markAsPristine();
  }

  /** Loads all dropdowns in parallel before initialising the form.
   *  Destinations are included in the forkJoin so they are guaranteed to be available
   *  before getById() patches destinationId values (avoids race where mat-select has no
   *  matching option yet and shows blank).
   *  Shippers/Consignees use DIRECT_ENTITIES_FOR_DD because the + button creates Direct Entities,
   *  so newly created entries appear in the same search endpoint. */
  private loadDropdownsAndInit() {
    forkJoin({
      businessSegments: this.service.get(Api.BUSINESS_SEGMENT_FOR_DD),
      customers: this.service.get(Api.EXTERNAL_ENTITY_FOR_DD, { customerFlag: true, activeFlag: true }),
      directEntities: this.service.get(Api.DIRECT_ENTITIES_FOR_DD, { size: 20, page: 0 }),
      destinations: this.service.get(Api.DESTINATION_NAMES_FOR_DD)
    }).subscribe({
      next: ({ businessSegments, customers, directEntities, destinations }) => {
        if (businessSegments) {
          this.businessSegments = businessSegments;
          this.filteredBusinessSegments.next(this.businessSegments);
        }
        if (customers) {
          this.customers = customers;
          this.filteredCustomers.next(this.customers);
        }
        if (directEntities?.contents) {
          // Shared initial list for both shipper and consignee columns
          this.initialShippers = directEntities.contents.slice();
          this.initialConsignees = directEntities.contents.slice();
          this.filteredRowShippers.next(this.initialShippers.slice());
          this.filteredRowConsignees.next(this.initialConsignees.slice());
        }
        if (destinations?.contents) {
          this.destinations = destinations.contents;
          this.filteredDestinations.next(this.destinations);
        }
        this.loadSharedCsrData();
        if (!this.hasSavedCsrState) {
          this.getById();
        }
      }
    });
  }

  getCustomerBranches(customerId: number) {
    this.service.get(Api.ENTITY_BRANCH_FOR_DD, { entiyId: customerId, activeFlag: true }).subscribe(res => {
      if (res) {
        this.branches = res;
        this.filteredBranches.next(this.branches);
      }
    });
  }

  filterRowDestinations() {
    this.rowDestinationSearchCtrl.valueChanges.pipe(
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => this.searching = true),
      exhaustMap(value => {
        if (!value) {
          this.filteredDestinations.next([...this.destinations]);
          this.searching = false;
          return of([]);
        }
        return this.service.get(Api.DESTINATION_NAMES_FOR_DD, { name: value }).pipe(
          map(res => res?.contents || []),
          tap(results => {
            const newItems = results.filter((r: { id: number }) => !this.destinations.some(d => d.id === r.id));
            this.destinations.push(...newItems);
            const selectedIds = [...new Set<number>(this.details.controls.map(r => r.get('destinationId')?.value).filter(Boolean))];
            const hiddenSelected = selectedIds
              .filter((id: number) => !results.some((r: { id: number }) => r.id === id))
              .map((id: number) => this.destinations.find(d => d.id === id))
              .filter((d: any) => d?.id && d?.name)
              .map((d: any) => ({ ...d, _hidden: true }));
            this.filteredDestinations.next([...results, ...hiddenSelected]);
            this.searching = false;
          }),
          catchError(() => { this.searching = false; return of([]); })
        );
      })
    ).subscribe();
  }

  private loadSharedCsrData() {
    const savedData = this.sharedFormService.getFormState('CSR');
    if (savedData?.isCsr) {
      this.sharedFormService.clearFormState('CSR');
      this.hasSavedCsrState = true;
      if (savedData.customerId) {
        this.getCustomerBranches(savedData.customerId);
      }
      this.form.patchValue(savedData, { emitEvent: false });
      this.patchDetails(savedData);

      // Ensure all existing row shippers/consignees/services that fall outside the initial 20 are visible.
      this.ensureSelectedEntitiesVisible();
      this.ensureSelectedServicesVisible();

      // When returning from Direct Entity creation the direct-entity-form stores the new
      // entity's id at the root level (shipperId / consigneeId).  Fetch that entity and
      // add it to the initial dropdown cache so the user can immediately find and select it
      // in the row without having to type a search term first.
      const newEntityIds: number[] = [savedData.shipperId, savedData.consigneeId].filter(Boolean);
      if (newEntityIds.length) {
        this.service.get(Api.DIRECT_ENTITIES_FOR_DD, { ids: newEntityIds }).subscribe(res => {
          if (!res?.contents) return;
          if (savedData.shipperId) {
            const entity = (res.contents as any[]).find(e => e.id === savedData.shipperId);
            if (entity && !this.initialShippers.some(s => s.id === entity.id)) {
              this.initialShippers.unshift(entity);
              this.filteredRowShippers.next(this.initialShippers.slice());
            }
          }
          if (savedData.consigneeId) {
            const entity = (res.contents as any[]).find(e => e.id === savedData.consigneeId);
            if (entity && !this.initialConsignees.some(c => c.id === entity.id)) {
              this.initialConsignees.unshift(entity);
              this.filteredRowConsignees.next(this.initialConsignees.slice());
            }
          }
        });
      }

      this.ensureSelectedDestinationsVisible();
    }
  }

  addDirectEntity(isConsignee: boolean) {
    const formData = {
      ...this.form.getRawValue(),
      isConsignee,
      isCsr: true
    };
    this.sharedFormService.setFormState('CSR', formData);
    this.router.navigate([APP_ROUTES.DIRECT_ENTITY_LIST + '/add'], { skipLocationChange: true });
  }

  addService() {
    const ref = this.dialog.open(ServiceFormComponent, {
      width: '650px',
      maxWidth: '90vw',
      disableClose: true,
      autoFocus: false
    });
    ref.afterClosed().subscribe(newService => {
      if (newService?.id && !this.initialServices.some(s => s.id === newService.id)) {
        this.initialServices.unshift(newService);
        this.filteredServices.next(this.initialServices.slice());
      }
    });
  }

  getServices() {
    this.service.get(Api.SERVICE_MASTER, { size: 20, page: 0 }).subscribe(res => {
      if (res?.contents) {
        this.initialServices = res.contents.slice();
        this.filteredServices.next(this.initialServices.slice());
      }
    });
  }

  filterBusinessSegments() {
    this.bsSearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      exhaustMap(value => {
        const searchStr = (!value || Array.isArray(value)) ? '' : String(value).toLowerCase();
        const filtered = !searchStr
          ? this.businessSegments.slice()
          : this.businessSegments.filter(bs => bs.name?.toLowerCase().includes(searchStr));
        this.filteredBusinessSegments.next(filtered);
        this.searching = false;
        return of([]);
      })
    ).subscribe(() => {}, () => { this.searching = false; });
  }

  filterCustomers() {
    this.customerSearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      exhaustMap(value => {
        const searchStr = (!value || Array.isArray(value)) ? '' : String(value).toLowerCase();
        const filtered = !searchStr
          ? this.customers.slice()
          : this.customers.filter(c => c.name?.toLowerCase().includes(searchStr));
        this.filteredCustomers.next(filtered);
        this.searching = false;
        return of([]);
      })
    ).subscribe(() => {}, () => { this.searching = false; });
  }

  filterRowShippers() {
    this.shipperSearchCtrl.valueChanges.pipe(
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => this.searching = true),
      exhaustMap(value => {
        if (!value) {
          // Show cached list (ensureSelectedEntitiesVisible already added selected ones to initialShippers)
          this.filteredRowShippers.next(this.initialShippers.slice());
          this.searching = false;
          return of([]);
        }
        // Use DIRECT_ENTITIES_FOR_DD — matches what the + Add Shipper button creates
        return this.service.get(Api.DIRECT_ENTITIES_FOR_DD, { 'name.contains': value }).pipe(
          map(res => res?.contents || []),
          tap(results => {
            const newItems = (results as any[]).filter(r => !this.initialShippers.some(e => e.id === r.id));
            this.initialShippers.push(...newItems);
            const selectedIds = [...new Set<number>(this.details.controls.map(row => row.get('shipperId')?.value).filter(Boolean))];
            const hiddenSelected = selectedIds
              .filter((id: number) => !results.some((r: { id: number }) => r.id === id))
              .map((id: number) => this.initialShippers.find(e => e.id === id))
              .filter((e: any) => e?.id && e?.name)
              .map((e: any) => ({ ...e, _hidden: true }));
            this.filteredRowShippers.next([...results, ...hiddenSelected]);
            this.searching = false;
          }),
          catchError(() => { this.searching = false; return of([]); })
        );
      })
    ).subscribe();
  }

  filterRowConsignees() {
    this.consigneeSearchCtrl.valueChanges.pipe(
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => this.searching = true),
      exhaustMap(value => {
        if (!value) {
          // Show cached list (ensureSelectedEntitiesVisible already added selected ones to initialConsignees)
          this.filteredRowConsignees.next(this.initialConsignees.slice());
          this.searching = false;
          return of([]);
        }
        // Use DIRECT_ENTITIES_FOR_DD — matches what the + Add Consignee button creates
        return this.service.get(Api.DIRECT_ENTITIES_FOR_DD, { 'name.contains': value }).pipe(
          map(res => res?.contents || []),
          tap(results => {
            const newItems = (results as any[]).filter(r => !this.initialConsignees.some(e => e.id === r.id));
            this.initialConsignees.push(...newItems);
            const selectedIds = [...new Set<number>(this.details.controls.map(row => row.get('consigneeId')?.value).filter(Boolean))];
            const hiddenSelected = selectedIds
              .filter((id: number) => !results.some((r: { id: number }) => r.id === id))
              .map((id: number) => this.initialConsignees.find(e => e.id === id))
              .filter((e: any) => e?.id && e?.name)
              .map((e: any) => ({ ...e, _hidden: true }));
            this.filteredRowConsignees.next([...results, ...hiddenSelected]);
            this.searching = false;
          }),
          catchError(() => { this.searching = false; return of([]); })
        );
      })
    ).subscribe();
  }

  /**
   * After patching edit-mode rows, ensures the already-selected shipper/consignee entities
   * are present in the shared dropdown list even if they fall outside the initial 20 records.
   * Uses DIRECT_ENTITIES_FOR_DD (same endpoint the + button uses) to fetch any missing IDs.
   */
  private ensureSelectedEntitiesVisible() {
    const shipperIds: number[] = [...new Set<number>(this.details.controls.map(r => r.get('shipperId')?.value).filter(Boolean))];
    const consigneeIds: number[] = [...new Set<number>(this.details.controls.map(r => r.get('consigneeId')?.value).filter(Boolean))];
    const missingShipperIds = shipperIds.filter(id => !this.initialShippers.some(e => e.id === id));
    const missingConsigneeIds = consigneeIds.filter(id => !this.initialConsignees.some(e => e.id === id));
    const allMissingIds = [...new Set([...missingShipperIds, ...missingConsigneeIds])];
    if (!allMissingIds.length) return;

    this.service.get(Api.DIRECT_ENTITIES_FOR_DD, { ids: allMissingIds }).subscribe(res => {
      if (!res?.contents) return;
      missingShipperIds.forEach(id => {
        const entity = res.contents.find((e: any) => e.id === id);
        if (entity && !this.initialShippers.some(e => e.id === entity.id)) this.initialShippers.unshift(entity);
      });
      missingConsigneeIds.forEach(id => {
        const entity = res.contents.find((e: any) => e.id === id);
        if (entity && !this.initialConsignees.some(e => e.id === entity.id)) this.initialConsignees.unshift(entity);
      });
      this.filteredRowShippers.next(this.initialShippers.slice());
      this.filteredRowConsignees.next(this.initialConsignees.slice());
    });
  }

  /**
   * After patching edit-mode rows, ensures the already-selected destinations are in the
   * dropdown list even if they fall outside the default first page.
   * Fetches missing IDs via DESTINATION_NAMES_FOR_DD so mat-select shows the correct value
   * instead of a blank — prevents the user from thinking nothing is selected and re-clicking.
   */
  private ensureSelectedDestinationsVisible() {
    const selectedIds: number[] = [...new Set<number>(this.details.controls
      .map(r => r.get('destinationId')?.value)
      .filter(Boolean))];
    const missingIds = selectedIds.filter(id => !this.destinations.some(d => d.id === id));
    if (!missingIds.length) return;

    this.service.get(Api.DESTINATION_NAMES_FOR_DD, { ids: missingIds }).subscribe(res => {
      if (!res?.contents?.length) return;
      const newItems = (res.contents as any[]).filter(r => !this.destinations.some(d => d.id === r.id));
      this.destinations.unshift(...newItems);
      this.filteredDestinations.next(this.destinations.slice());
    });
  }

  /**
   * After patching edit-mode rows, ensures the already-selected services are in the
   * dropdown list even if they fall outside the initial 20 records fetched on load.
   */
  private ensureSelectedServicesVisible() {
    const serviceIds: number[] = [...new Set(
      this.details.controls.map(r => r.get('serviceId')?.value).filter(Boolean) as number[]
    )];
    const missingIds = serviceIds.filter(id => !this.initialServices.some(s => s.id === id));
    if (!missingIds.length) return;

    forkJoin(missingIds.map(id =>
      this.service.get(Api.SERVICE_MASTER, { 'id.equals': id, size: 1, page: 0 })
    )).subscribe((responses: any[]) => {
      responses.forEach(res => {
        const item = res?.contents?.[0];
        if (item && !this.initialServices.some(s => s.id === item.id)) {
          this.initialServices.unshift(item);
        }
      });
      this.filteredServices.next(this.initialServices.slice());
    });
  }

  filterRowServices() {
    this.serviceSearchCtrl.valueChanges.pipe(
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => this.searching = true),
      exhaustMap(value => {
        if (!value) {
          this.filteredServices.next(this.initialServices.slice());
          this.searching = false;
          return of([]);
        }
        return this.service.get(Api.SERVICE_MASTER, { 'serviceDescription.contains': value, size: 20, page: 0 }).pipe(
          map(res => res?.contents || []),
          tap(results => {
            const newItems = (results as any[]).filter(r => !this.initialServices.some(s => s.id === r.id));
            this.initialServices.push(...newItems);
            const selectedIds = [...new Set<number>(this.details.controls.map(row => row.get('serviceId')?.value).filter(Boolean))];
            const hiddenSelected = selectedIds
              .filter((id: number) => !results.some((r: { id: number }) => r.id === id))
              .map((id: number) => this.initialServices.find(s => s.id === id))
              .filter((s: any) => s?.id && s?.serviceCode)
              .map((s: any) => ({ ...s, _hidden: true }));
            this.filteredServices.next([...results, ...hiddenSelected]);
            this.searching = false;
          }),
          catchError(() => { this.searching = false; return of([]); })
        );
      })
    ).subscribe();
  }

  filterBranches() {
    this.branchSearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      exhaustMap(value => {
        const searchStr = (!value || Array.isArray(value)) ? '' : String(value).toLowerCase();
        const filtered = !searchStr
          ? this.branches.slice()
          : this.branches.filter(b => b.name?.toLowerCase().includes(searchStr));
        this.filteredBranches.next(filtered);
        this.searching = false;
        return of([]);
      })
    ).subscribe(() => {}, () => { this.searching = false; });
  }

  getSelectedName(list: any[], id: number): string {
    if (!id || !list) return '';
    return list.find(item => item.id === id)?.name || '';
  }

  getSelectedServiceLabel(id: number): string {
    if (!id) return '';
    const s = this.initialServices.find(item => item.id === id);
    if (!s) return '';
    return (s.serviceCode || '') + (s.serviceDescription ? ' - ' + s.serviceDescription : '');
  }

  getSaveBtnCondition(): boolean {
    if (this.disableSubmit
      || (!this.editPermission && this.id.value)
      || (!this.addPermission && !this.id.value)
      || !this.form.dirty) {
      return false;
    }
    return true;
  }

  public getAllPermissions() {
    const permissionCodes = [
      PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE_ADD_PERMISSION,
      PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE_EDIT_PERMISSION
    ];
    const permissionsWithShowValue: IPermissionWithShow[] =
      this.permissionService.checkPermissions(permissionCodes, PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE);
    this.addPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE_ADD_PERMISSION })?.show ? true : false;
    this.editPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.CUSTOMER_SERVICE_REQUIREMENT_MODULE_EDIT_PERMISSION })?.show ? true : false;
  }

  get id() { return this.form.get('id'); }
  get businessSegmentId() { return this.form.get('businessSegmentId'); }
  get customerId() { return this.form.get('customerId'); }
  get customerBranchId() { return this.form.get('customerBranchId'); }
  get details(): FormArray { return this.form.get('customerServiceRequirementDetails') as FormArray; }
}
